/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import '../Dashboard.css';

// CHANGE 1: Import the three new feature components.
// Each receives token as a prop, consistent with your auth pattern.
import KanbanBoard from './KanbanBoard';
import ScoringWeights from './ScoringWeights';
import SavedAnalysesPanel from '../components/SavedAnalysesPanel';
import InvitePanel from './InvitePanel';
import MFASetup from './MFASetup';
import FirmBranding from './FirmBranding';
import Terminal from './Terminal';
import Searcher from '../components/Searcher';
import EmergingMarkets from '../components/EmergingMarkets';
import Peers from '../components/Peers';
import Firms from '../components/Firms';
import DailyBrief from '../components/DailyBrief';
import Library from '../components/Library';

const API = 'https://coast-terminal-production.up.railway.app';

interface Props {
  token: string;
  onLogout: () => void;
  onOpenBoard?: () => void;
  onOpenMap?:   () => void;
}

// CHANGE 2: Add 'kanban' to the Tab union type.
type Tab = 'market' | 'searcher' | 'emerging' | 'peers' | 'firms' | 'kanban' | 'admin';

// CHANGE 3: Add 'scoring' to the AdminTab union type.
type AdminTab = 'stats' | 'users' | 'deals' | 'scoring' | 'invites' | 'mfa' | 'branding';

type UserRole = 'analyst' | 'reviewer' | 'admin';

interface Deal { id: number; name: string; location: string; units: number; purchase_price: number; noi: number; market_rent: number; occupancy: number; year_built: number; asset_class: string; status: string; underwriting: string; memo: string; created_at: string; }
interface Note { id: number; username: string; note: string; created_at: string; }
interface Activity { id: number; username: string; action: string; detail: string; created_at: string; }
interface DealForm { property_name: string; location: string; units: string; purchase_price: string; noi: string; market_rent: string; occupancy: string; year_built: string; asset_class: string; }

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return width;
}

export default function Dashboard({ token, onLogout, onOpenBoard, onOpenMap }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('market');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const openLibrary = () => setLibraryOpen(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const width = useWindowWidth();
  const isMobile = width < 768;
  const isTablet = width < 1024;

  const username = (() => {
    try { return JSON.parse(atob(token.split('.')[1])).sub || 'analyst'; }
    catch { return 'analyst'; }
  })();

  const [userRole, setUserRole] = useState<UserRole>('analyst');
  const [userName, setUserName] = useState('');
  const [firmName, setFirmName] = useState('Coast');
  const [firmPrimaryColor, setFirmPrimaryColor] = useState('#b8956a');
  const isReviewer = userRole === 'reviewer';
  const isAdmin = userRole === 'admin';
  const canEdit = !isReviewer;

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(res => {
      setUserRole(res.data.role as UserRole);
      setUserName(res.data.name || username);
    }).catch(() => {});
    axios.get(`${API}/firms/me`, { headers }).then(res => {
      setFirmName(res.data.name || 'Coast');
      setFirmPrimaryColor(res.data.primary_color || '#b8956a');
    }).catch(() => {});
  }, []); // eslint-disable-line

  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<{ role: string; text: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);

  const emptyDeal: DealForm = { property_name: '', location: '', units: '', purchase_price: '', noi: '', market_rent: '', occupancy: '', year_built: '', asset_class: 'Class A' };
  const [deal, setDeal] = useState<DealForm>(emptyDeal);
  const [uwResult, setUwResult] = useState('');
  const [uwLoading, setUwLoading] = useState(false);
  const [memoResult, setMemoResult] = useState('');
  const [memoLoading, setMemoLoading] = useState(false);
  const [uwEngineResult, setUwEngineResult] = useState<any>(null); // Part 4 engine result
  const [saveMsg, setSaveMsg] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [rentRollData, setRentRollData] = useState<any>(null);
  const [dealScore, setDealScore] = useState<any>(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [compsData, setCompsData] = useState<any>(null);
  const [compsLoading, setCompsLoading] = useState(false);

  const [msa, setMsa] = useState('');
  const [marketResult, setMarketResult] = useState('');
  const [marketLoading, setMarketLoading] = useState(false);

  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);
  const [compareResult, setCompareResult] = useState('');
  const [compareLoading, setCompareLoading] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteInput, setNoteInput] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterLocation, setFilterLocation] = useState('All');
  const [filterMinCap, setFilterMinCap] = useState('');
  const [filterMaxCap, setFilterMaxCap] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'cap_rate' | 'price' | 'units'>('date');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');

  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [adminDeals, setAdminDeals] = useState<any[]>([]);
  const [adminStats, setAdminStats] = useState<any>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminTab, setAdminTab] = useState<AdminTab>('stats');

  useEffect(() => { loadDeals(); }, []); // eslint-disable-line

  const totalUnits = deals.reduce((a, d) => a + (d.units || 0), 0);
  const uniqueMSAs = new Set(deals.filter(d => d.location).map(d => d.location.split(',')[0].trim())).size;

  const filteredDeals = useMemo(() => {
    let result = [...deals];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d => d.name?.toLowerCase().includes(q) || d.location?.toLowerCase().includes(q) || d.asset_class?.toLowerCase().includes(q));
    }
    if (filterStatus !== 'All') result = result.filter(d => d.status === filterStatus);
    if (filterLocation !== 'All') result = result.filter(d => d.location?.includes(filterLocation));
    if (filterMinCap || filterMaxCap) {
      result = result.filter(d => {
        if (!d.noi || !d.purchase_price) return false;
        const cap = (d.noi / d.purchase_price) * 100;
        if (filterMinCap && cap < +filterMinCap) return false;
        if (filterMaxCap && cap > +filterMaxCap) return false;
        return true;
      });
    }
    result.sort((a, b) => {
      let aVal = 0, bVal = 0;
      if (sortBy === 'date') { aVal = new Date(a.created_at).getTime(); bVal = new Date(b.created_at).getTime(); }
      if (sortBy === 'cap_rate') { aVal = a.noi && a.purchase_price ? a.noi / a.purchase_price : 0; bVal = b.noi && b.purchase_price ? b.noi / b.purchase_price : 0; }
      if (sortBy === 'price') { aVal = a.purchase_price || 0; bVal = b.purchase_price || 0; }
      if (sortBy === 'units') { aVal = a.units || 0; bVal = b.units || 0; }
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return result;
  }, [deals, searchQuery, filterStatus, filterLocation, filterMinCap, filterMaxCap, sortBy, sortDir]);

  const uniqueLocations = useMemo(() =>
    Array.from(new Set(deals.filter(d => d.location).map(d => d.location.split(',')[0].trim()))).sort()
  , [deals]);

  const exportToExcel = () => {
    const rows = filteredDeals.map(d => ({
      'Property Name': d.name,
      'Location': d.location,
      'Asset Class': d.asset_class,
      'Units': d.units,
      'Purchase Price ($)': d.purchase_price,
      'NOI ($)': d.noi,
      'Cap Rate (%)': d.noi && d.purchase_price ? +((d.noi / d.purchase_price) * 100).toFixed(2) : '',
      'Market Rent/Unit ($)': d.market_rent,
      'Occupancy (%)': d.occupancy,
      'Year Built': d.year_built,
      'Status': d.status,
      'Date Added': new Date(d.created_at).toLocaleDateString(),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 28 }, { wch: 22 }, { wch: 12 }, { wch: 8 },
      { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
      { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Deal Bank');
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `coast_deals_${date}.xlsx`);
  };

  const loadAdminData = useCallback(async () => {
    setAdminLoading(true);
    try {
      const [s, u, d] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/deals`, { headers }),
      ]);
      setAdminStats(s.data); setAdminUsers(u.data); setAdminDeals(d.data);
    } catch { console.error('Admin load failed'); }
    finally { setAdminLoading(false); }
  }, []); // eslint-disable-line

  const loadDeals = useCallback(async () => {
    setDealsLoading(true);
    try { const res = await axios.get(`${API}/deals/list`, { headers }); setDeals(res.data); }
    catch { console.error('Deals load failed'); }
    finally { setDealsLoading(false); }
  }, []); // eslint-disable-line

  // No-op: Wish List / Deal Bank tab was removed. Kanban now just opens the
  // selected deal locally without cross-tab navigation.
  const handleKanbanDealClick = useCallback(async (dealId: number) => {
    const found = deals.find(d => d.id === dealId);
    if (found && selectedDeal?.id !== dealId) await expandDeal(found);
  }, [deals, selectedDeal]); // eslint-disable-line

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput; setChatInput('');
    setChatHistory(h => [...h, { role: 'you', text: msg }]); setChatLoading(true);
    try {
      const res = await axios.post(`${API}/analyst/chat`, { message: msg }, { headers });
      setChatHistory(h => [...h, { role: 'analyst', text: res.data.response }]);
    } catch { setChatHistory(h => [...h, { role: 'analyst', text: 'Connection error.' }]); }
    finally { setChatLoading(false); }
  };

  const runUnderwrite = async () => {
    setUwLoading(true); setUwResult(''); setDealScore(null); setCompsData(null);
    try {
      const res = await axios.post(`${API}/analyst/underwrite`, { ...deal, units: +deal.units, purchase_price: +deal.purchase_price, noi: +deal.noi, market_rent: +deal.market_rent, occupancy: +deal.occupancy, year_built: +deal.year_built }, { headers });
      setUwResult(res.data.underwriting);
    } catch { setUwResult('Error running underwriting.'); }
    finally { setUwLoading(false); }
  };

  const runMemo = async () => {
    setMemoLoading(true); setMemoResult('');
    try {
      const res = await axios.post(`${API}/analyst/memo`, { ...deal, units: +deal.units, purchase_price: +deal.purchase_price, noi: +deal.noi, market_rent: +deal.market_rent, occupancy: +deal.occupancy, year_built: +deal.year_built }, { headers });
      setMemoResult(res.data.memo);
    } catch { setMemoResult('Error generating memo.'); }
    finally { setMemoLoading(false); }
  };

  const runMarket = async () => {
    setMarketLoading(true); setMarketResult('');
    try { const res = await axios.post(`${API}/analyst/market`, { msa }, { headers }); setMarketResult(res.data.analysis); }
    catch { setMarketResult('Error fetching market analysis.'); }
    finally { setMarketLoading(false); }
  };

  const saveDeal = async (status = 'Active') => {
    try {
      await axios.post(`${API}/deals/save`, { name: deal.property_name || 'Untitled Deal', location: deal.location, units: +deal.units || 0, purchase_price: +deal.purchase_price || 0, noi: +deal.noi || 0, market_rent: +deal.market_rent || 0, occupancy: +deal.occupancy || 0, year_built: +deal.year_built || 0, asset_class: deal.asset_class, status, underwriting: uwResult, memo: memoResult }, { headers });
      setSaveMsg('Saved.'); setTimeout(() => setSaveMsg(''), 3000); loadDeals();
    } catch { setSaveMsg('Error saving.'); }
  };

  const handleFileUpload = async (file: File) => {
    setUploadLoading(true); setUploadMsg('Reading...'); setRentRollData(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/rent-roll`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        const d = res.data.data;
        setDeal({ property_name: d.property_name || '', location: d.location || '', units: d.units?.toString() || '', purchase_price: '', noi: d.noi?.toString() || '', market_rent: d.market_rent?.toString() || '', occupancy: d.occupancy?.toString() || '', year_built: d.year_built?.toString() || '', asset_class: 'Class A' });
        setRentRollData(d); setUploadMsg(d.summary || 'Parsed.');
      } else { setUploadMsg('Could not parse.'); }
    } catch { setUploadMsg('Upload failed.'); }
    finally { setUploadLoading(false); }
  };

  const scoreDeal = async () => {
    if (!deal.purchase_price || !deal.noi) return;
    setScoreLoading(true);
    try { const res = await axios.post(`${API}/analyst/score`, { location: deal.location, units: +deal.units || 0, purchase_price: +deal.purchase_price, noi: +deal.noi, market_rent: +deal.market_rent || 0, occupancy: +deal.occupancy || 0, year_built: +deal.year_built || 0, asset_class: deal.asset_class }, { headers }); setDealScore(res.data); }
    catch { console.error('Scoring failed'); }
    finally { setScoreLoading(false); }
  };

  const runComps = async () => {
    if (!deal.location) return;
    setCompsLoading(true); setCompsData(null);
    try { const res = await axios.post(`${API}/analyst/comps`, { location: deal.location, units: +deal.units || 0, purchase_price: +deal.purchase_price || 0, asset_class: deal.asset_class }, { headers }); setCompsData(res.data); }
    catch { console.error('Comps failed'); }
    finally { setCompsLoading(false); }
  };

  const updateStatus = async (id: number, status: string) => { await axios.patch(`${API}/deals/${id}/status`, { status }, { headers }); loadDeals(); if (selectedDeal?.id === id) await fetchDealData(id); };
  const deleteDeal = async (id: number) => { await axios.delete(`${API}/deals/${id}`, { headers }); if (selectedDeal?.id === id) { setSelectedDeal(null); setNotes([]); setActivity([]); } loadDeals(); };

  const downloadPDF = async (type: 'underwriting' | 'memo') => {
    try {
      const res = await axios.post(`${API}/pdf/generate`, { property_name: deal.property_name || 'Deal', location: deal.location, units: +deal.units || 0, purchase_price: +deal.purchase_price || 0, noi: +deal.noi || 0, occupancy: +deal.occupancy || 0, year_built: +deal.year_built || 0, asset_class: deal.asset_class, underwriting: uwResult, memo: memoResult, doc_type: type }, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.setAttribute('download', `coast_${(deal.property_name || 'deal').replace(/\s+/g, '_').toLowerCase()}_${type}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch { alert('PDF failed.'); }
  };

  const toggleCompare = (id: number) => setSelectedForCompare(p => p.includes(id) ? p.filter(x => x !== id) : p.length < 3 ? [...p, id] : p);

  const runCompare = async () => {
    if (selectedForCompare.length < 2) return;
    setCompareLoading(true); setCompareResult('');
    try { const res = await axios.post(`${API}/analyst/compare`, { deals: deals.filter(d => selectedForCompare.includes(d.id)) }, { headers }); setCompareResult(res.data.comparison); }
    catch { setCompareResult('Comparison failed.'); }
    finally { setCompareLoading(false); }
  };

  const fetchDealData = async (dealId: number) => {
    const [notesRes, activityRes] = await Promise.all([
      axios.get(`${API}/notes/${dealId}`, { headers }),
      axios.get(`${API}/activity/${dealId}`, { headers }),
    ]);
    setNotes(notesRes.data); setActivity(activityRes.data);
  };

  const expandDeal = async (d: Deal) => {
    if (selectedDeal?.id === d.id) { setSelectedDeal(null); setNotes([]); setActivity([]); return; }
    setSelectedDeal(d); setNoteInput(''); setNotesLoading(true); setActivityLoading(true);
    try { await fetchDealData(d.id); }
    catch { setNotes([]); setActivity([]); }
    finally { setNotesLoading(false); setActivityLoading(false); }
  };

  const addNote = async () => {
    if (!noteInput.trim() || !selectedDeal) return;
    try { await axios.post(`${API}/notes/add`, { deal_id: selectedDeal.id, note: noteInput }, { headers }); setNoteInput(''); await fetchDealData(selectedDeal.id); }
    catch { console.error('Note failed'); }
  };

  const deleteNote = async (noteId: number) => {
    if (!selectedDeal) return;
    await axios.delete(`${API}/notes/${noteId}`, { headers });
    await fetchDealData(selectedDeal.id);
  };

  const toggleUser = async (id: number) => { await axios.patch(`${API}/admin/users/${id}/toggle`, {}, { headers }); loadAdminData(); };
  const setUserRoleAdmin = async (id: number, role: string) => { await axios.patch(`${API}/admin/users/${id}/set-role`, { role }, { headers }); loadAdminData(); };
  const navTab = (tab: Tab) => { setActiveTab(tab); setMobileNavOpen(false); if (tab === 'admin') loadAdminData(); };

  const renderAnalysis = (text: string) => {
    const serifFont = "'Fraunces', Georgia, serif";
  const bodyFont = "'EB Garamond', Georgia, serif";
    // Detect table blocks — consecutive lines starting with |
    const lines = text.split('\n');
    const result: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      const t = lines[i].trim();

      // Table detection — collect all consecutive pipe lines
      if (t.startsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        // Filter out separator lines (|---|---|)
        const allLines = tableLines.filter(l => !/^\|[\s|:-]+\|$/.test(l));
        if (allLines.length >= 2) {
          const headers = allLines[0].split('|').filter(c => c.trim()).map(c => c.trim());
          const rows = allLines.slice(1).map(l => l.split('|').filter(c => c.trim()).map(c => c.trim()));
          result.push(
            <div key={i} style={{ overflowX: 'auto', marginBottom: '16px', marginTop: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafaf8' }}>
                    {headers.map((h, hi) => (
                      <th key={hi} style={{ padding: '8px 14px', textAlign: 'left', fontSize: '9px', color: gold, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri} style={{ borderBottom: `1px solid ${border}` }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{ padding: '9px 14px', fontSize: '13px', color: ci === 0 ? textPrimary : textSecondary, fontFamily: serifFont, lineHeight: 1.6 }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }

      if (!t) { result.push(<div key={i} style={{ height: '10px' }} />); i++; continue; }

      if (t.startsWith('## ') || t.startsWith('# '))  {
        result.push(<div key={i} style={{ fontSize: '11px', fontWeight: 800, color: gold, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '28px', marginBottom: '10px', paddingBottom: '8px', borderBottom: `1px solid ${border}` }}>{t.replace(/^#+\s/, '')}</div>);
        i++; continue;
      }
      if (t.startsWith('**') && t.endsWith('**')) {
        result.push(<div key={i} style={{ fontSize: '14px', fontWeight: 700, color: textPrimary, marginTop: '14px', marginBottom: '6px', fontFamily: serifFont }}>{t.replace(/\*\*/g, '')}</div>);
        i++; continue;
      }
      if (t.startsWith('---') || t.startsWith('===')) {
        result.push(<div key={i} style={{ height: '1px', backgroundColor: border, margin: '16px 0' }} />);
        i++; continue;
      }
      if (t.startsWith('- ') || t.startsWith('• ')) {
        result.push(
          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: gold, marginTop: '10px', flexShrink: 0 }} />
            <div style={{ fontSize: '14px', color: textSecondary, lineHeight: 1.8, fontFamily: serifFont }} dangerouslySetInnerHTML={{ __html: t.replace(/^[-•]\s/, '').replace(/\*\*([^*]+)\*\*/g, '<strong style="color:#E8DCC8;font-weight:700">$1</strong>') }} />
          </div>
        );
        i++; continue;
      }
      if (t.match(/^\d+\./)) {
        const num = t.match(/^(\d+)\./)?.[1];
        result.push(
          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: gold, minWidth: '18px', marginTop: '4px' }}>{num}.</div>
            <div style={{ fontSize: '14px', color: textSecondary, lineHeight: 1.8, fontFamily: serifFont }} dangerouslySetInnerHTML={{ __html: t.replace(/^\d+\.\s/, '').replace(/\*\*([^*]+)\*\*/g, '<strong style="color:#E8DCC8;font-weight:700">$1</strong>') }} />
          </div>
        );
        i++; continue;
      }
      result.push(
        <div key={i} style={{ fontSize: '14px', color: textSecondary, lineHeight: 1.9, marginBottom: '4px', fontFamily: serifFont }}
          dangerouslySetInnerHTML={{ __html: t.replace(/\*\*([^*]+)\*\*/g, '<strong style="color:#E8DCC8;font-weight:700">$1</strong>') }} />
      );
      i++;
    }
    return result;
  };

  const renderActivityTimeline = () => {
    const cfg: Record<string, { color: string; icon: string }> = {
      created: { color: green, icon: '●' },
      status_changed: { color: gold, icon: '◆' },
      note_added: { color: textSecondary, icon: '○' },
      underwritten: { color: gold, icon: '▲' },
      memo_generated: { color: textSecondary, icon: '▲' },
    };
    if (activityLoading) return <div style={{ color: textMuted, fontSize: '11px' }}>Loading...</div>;
    if (activity.length === 0) return <div style={{ color: textMuted, fontSize: '11px' }}>No activity yet.</div>;
    return (
      <div style={{ position: 'relative' }}>
        {activity.map((a, i) => {
          const c = cfg[a.action] || { color: textMuted, icon: '●' };
          return (
            <div key={a.id} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '14px', position: 'relative' }}>
              {i < activity.length - 1 && <div style={{ position: 'absolute', left: '5px', top: '16px', width: '1px', height: 'calc(100% + 2px)', backgroundColor: border }} />}
              <div style={{ fontSize: '11px', color: c.color, marginTop: '2px', flexShrink: 0, zIndex: 1, lineHeight: 1 }}>{c.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '12px', color: textPrimary, marginBottom: '2px' }}>{a.detail}</div>
                <div style={{ fontSize: '9px', color: textMuted }}>{a.username} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Design tokens
  const gold = firmPrimaryColor || '#b8956a';
  const goldDim = 'rgba(201,169,110,0.12)';
  const goldBorder = 'rgba(201,169,110,0.30)';
  const red = '#CC2200';
  const green = '#1A7A4A';
  const blue = '#1A5A8A';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const cream = '#f5efe6';
  const creamDim = 'rgba(245,239,230,0.07)';
  const serifFont = "'Fraunces', Georgia, serif";
  const bodyFont = "'EB Garamond', Georgia, serif";

  const roleColors: Record<UserRole, string> = { analyst: textSecondary, reviewer: blue, admin: gold };
  const roleColor = roleColors[userRole] || textSecondary;
  const px = isMobile ? '16px' : isTablet ? '28px' : '48px';
  const pad = isMobile ? '20px 16px' : isTablet ? '28px 28px' : '40px 48px';

  const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', backgroundColor: '#f2ede6', border: `1px solid ${border}`, padding: '10px 12px', color: textPrimary, fontSize: '13px', outline: 'none', fontFamily: bodyFont };
  const btn = (active = true): React.CSSProperties => ({ backgroundColor: active ? '#0d3d5c' : '#f2ede6', color: active ? '#ffffff' : textMuted, border: 'none', borderRadius: '2px', padding: isMobile ? '10px 16px' : '11px 28px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: active ? 'pointer' : 'default', fontFamily: 'inherit', whiteSpace: 'nowrap' });
  const outBtn: React.CSSProperties = { backgroundColor: 'transparent', color: '#12526e', border: `1px solid ${border}`, borderRadius: '2px', padding: isMobile ? '10px 14px' : '11px 20px', fontWeight: 700, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const goldBtn: React.CSSProperties = { backgroundColor: 'transparent', color: gold, border: `1px solid ${gold}`, borderRadius: '2px', padding: isMobile ? '10px 14px' : '11px 20px', fontWeight: 700, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };
  const greenBtn: React.CSSProperties = { backgroundColor: 'transparent', color: green, border: `1px solid ${green}`, borderRadius: '2px', padding: '6px 12px', fontWeight: 700, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' };
  const redBtn: React.CSSProperties = { backgroundColor: 'transparent', color: red, border: `1px solid ${red}`, borderRadius: '2px', padding: '6px 12px', fontWeight: 700, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' };
  const card: React.CSSProperties = { backgroundColor: '#ffffff', border: `1px solid ${border}`, padding: isMobile ? '20px 16px' : '28px 32px' };
  const fl: React.CSSProperties = { fontSize: '9px', color: textMuted, marginBottom: '6px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' };
  const sl: React.CSSProperties = { fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700, fontFamily: 'sans-serif' };
  const pt: React.CSSProperties = { fontSize: isMobile ? '24px' : '32px', fontWeight: 600, color: textPrimary, letterSpacing: '-0.5px', marginBottom: '6px', fontFamily: serifFont };
  const ps: React.CSSProperties = { fontSize: '15px', color: textMuted, marginBottom: '28px', fontFamily: bodyFont, lineHeight: 1.6 };
  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${border}` };
  const td: React.CSSProperties = { padding: '12px 14px', fontSize: '12px', color: textSecondary, borderBottom: `1px solid ${border}` };
  const tt = { backgroundColor: '#fafaf8', border: `1px solid ${border}`, borderRadius: '2px', color: textPrimary, fontSize: '11px' };
  const scoreColor = (s: number) => s >= 70 ? green : s >= 50 ? gold : red;

  const STATUS_OPTIONS = ['Active', 'Negotiate', 'Approved', 'Pass', 'Rejected'];
  const STATUS_COLORS: Record<string, string> = { Active: green, Negotiate: gold, Approved: '#00AA44', Pass: textMuted, Rejected: red };
  const MARKETS = ['Atlanta, GA', 'Charlotte, NC', 'Dallas, TX', 'Denver, CO', 'Seattle, WA', 'Kansas City, MO', 'Las Vegas, NV', 'Tampa, FL', 'Washington, D.C.', 'Los Angeles, CA'];
  const FIELDS: [string, keyof DealForm, string][] = [
    ['Property Name', 'property_name', 'e.g. Alton District'],
    ['Location', 'location', 'e.g. Washington, D.C.'],
    ['Units', 'units', 'e.g. 125'],
    ['Purchase Price ($)', 'purchase_price', 'e.g. 45000000'],
    ['Current NOI ($)', 'noi', 'e.g. 1800000'],
    ['Market Rent / Unit', 'market_rent', 'e.g. 2800'],
    ['Occupancy (%)', 'occupancy', 'e.g. 94'],
    ['Year Built', 'year_built', 'e.g. 2019'],
  ];

  // CHANGE 5: Add 'kanban' to NAV. Placed between Deal Bank and Portfolio —
  // it's the active-pipeline view sitting between "where deals are stored"
  // and "portfolio-level reporting".
  const NAV: { id: Tab; label: string; adminOnly?: boolean; hideFromReviewer?: boolean }[] = [
    { id: 'market', label: 'Terminal' },
    { id: 'searcher', label: 'Searcher' },
    { id: 'emerging', label: 'Emerging' },
    { id: 'peers', label: 'Peers' },
    { id: 'firms', label: 'Firms' },
    { id: 'kanban', label: 'Pipeline' },
    { id: 'admin', label: 'Admin', adminOnly: true },
  ];

  const ReviewerBanner = () => (
    <div style={{ backgroundColor: 'rgba(26,90,138,0.15)', border: `1px solid rgba(26,90,138,0.4)`, borderLeft: `3px solid ${blue}`, padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ fontSize: '9px', color: '#4A9ACA', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700 }}>Reviewer Mode</div>
      <div style={{ fontSize: '11px', color: textSecondary }}>Read-only access. You can approve or reject deals.</div>
    </div>
  );

  const ExpandedDealPanel = ({ d }: { d: Deal }) => (
    <div style={{ backgroundColor: bg, borderLeft: `2px solid ${gold}`, padding: isMobile ? '16px' : '24px 28px' }}>
      {(d.underwriting || d.memo) && (
        <div style={{ marginBottom: '24px' }}>
          {d.underwriting && <><div style={{ ...sl, marginBottom: '10px' }}>Underwriting Analysis</div><div style={{ marginBottom: '16px' }}>{renderAnalysis(d.underwriting)}</div></>}
          {d.memo && <><div style={{ ...sl, marginBottom: '10px', marginTop: '20px' }}>Investment Memo</div><div>{renderAnalysis(d.memo)}</div></>}
        </div>
      )}
      {isReviewer && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', padding: '14px 16px', backgroundColor: 'rgba(26,90,138,0.08)', border: `1px solid rgba(26,90,138,0.25)` }}>
          <div style={{ fontSize: '11px', color: textSecondary, flex: 1 }}>Review Decision:</div>
          <button onClick={() => updateStatus(d.id, 'Approved')} style={greenBtn}>✓ Approve</button>
          <button onClick={() => updateStatus(d.id, 'Rejected')} style={redBtn}>✕ Reject</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', borderTop: `1px solid ${border}`, paddingTop: '20px' }}>
        <div>
          <div style={{ ...sl, marginBottom: '14px' }}>Activity Log</div>
          {renderActivityTimeline()}
        </div>
        <div>
          <div style={{ ...sl, marginBottom: '14px' }}>Deal Notes</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input style={{ ...inp, flex: 1 }} placeholder="Add a note..." value={noteInput} onChange={e => setNoteInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} />
            <button onClick={addNote} style={{ ...btn(), padding: '10px 16px', fontSize: '11px' }}>Add</button>
          </div>
          {notesLoading && <div style={{ color: textMuted, fontSize: '11px' }}>Loading...</div>}
          {notes.length === 0 && !notesLoading && <div style={{ color: textMuted, fontSize: '11px' }}>No notes yet.</div>}
          {notes.map(n => (
            <div key={n.id} style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '10px 14px', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '12px', color: textPrimary, marginBottom: '3px' }}>{n.note}</div>
                <div style={{ fontSize: '9px', color: textMuted }}>{n.username} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
              {canEdit && <button onClick={() => deleteNote(n.id)} style={{ backgroundColor: 'transparent', border: 'none', color: textMuted, cursor: 'pointer', fontSize: '12px', marginLeft: '12px' }}>✕</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Nav shape renderer ──────────────────────────────────────────────────────
  const NAV_SHAPES: Record<string, string> = {
    market:    'triangle',
    searcher:  'square-sm',
    emerging:  'diamond',
    peers:     'circle-sm',
    firms:     'triangle',
    kanban:    'circle-sm',
    admin:     'circle',
  };

  const NavShape = ({ shape, active }: { shape: string; active: boolean }) => {
    const activeColor = '#b8956a';
    const inactiveColor = '#1a6688';
    const c = active ? activeColor : inactiveColor;

    if (shape === 'circle' || shape === 'circle-sm') {
      const sz = shape === 'circle-sm' ? 12 : 15;
      const innerSz = sz - 5;
      return (
        <div style={{ width: sz, height: sz, borderRadius: '50%', border: `1.5px solid ${c}`, position: 'relative', transition: 'border-color 0.19s', flexShrink: 0 }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', width: innerSz, height: innerSz, borderRadius: '50%', background: activeColor, transform: `translate(-50%, -50%) scale(${active ? 1 : 0})`, opacity: active ? 1 : 0, transition: 'all 0.19s cubic-bezier(0.34,1.56,0.64,1)' }} />
        </div>
      );
    }
    if (shape === 'square' || shape === 'square-sm') {
      const sz = shape === 'square-sm' ? 11 : 14;
      const innerSz = sz - 4;
      return (
        <div style={{ width: sz, height: sz, border: `1.5px solid ${c}`, position: 'relative', transition: 'border-color 0.19s', flexShrink: 0 }}>
          <div style={{ position: 'absolute', inset: 2, background: activeColor, opacity: active ? 1 : 0, transform: active ? 'scale(1)' : 'scale(0.2)', transition: 'all 0.19s cubic-bezier(0.34,1.56,0.64,1)' }} />
        </div>
      );
    }
    // Triangle — hollow outline using SVG so we can stroke it without fill
    if (shape === 'triangle') {
      return (
        <svg width="16" height="14" viewBox="0 0 16 14" style={{ flexShrink: 0 }}>
          <polygon
            points="8,1 15,13 1,13"
            fill={active ? activeColor : 'none'}
            stroke={c}
            strokeWidth="1.5"
            strokeLinejoin="round"
            style={{ transition: 'fill 0.19s, stroke 0.19s' }}
          />
        </svg>
      );
    }
    // Diamond — hollow outline using SVG
    if (shape === 'diamond') {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
          <polygon
            points="7,1 13,7 7,13 1,7"
            fill={active ? activeColor : 'none'}
            stroke={c}
            strokeWidth="1.5"
            strokeLinejoin="round"
            style={{ transition: 'fill 0.19s, stroke 0.19s' }}
          />
        </svg>
      );
    }
    return null;
  };

  const filteredNav = NAV.filter(n => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.hideFromReviewer && isReviewer) return false;
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg, color: textPrimary, fontFamily: "'EB Garamond', Georgia, serif" }}>

      {/* ── TOP BAR ── */}
      <div className="top-bar" style={{ position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="top-bar-left">
          {/* Wordmark */}
          <div className="wordmark">
            <div className="wordmark-text">COAST</div>
            {!isMobile && <div className="wordmark-tagline">coast to coast</div>}
          </div>

          {/* Portfolio stats */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: '1px', backgroundColor: border, marginLeft: '20px' }}>
              {[[deals.length.toString(), 'DEALS'], [totalUnits.toLocaleString(), 'UNITS'], [uniqueMSAs.toString(), 'MSAS']].map(([num, lbl]) => (
                <div key={lbl} style={{ textAlign: 'center', padding: '0 16px', height: '52px', display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: surface }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: gold, lineHeight: 1, fontFamily: "'Fraunces', Georgia, serif" }}>{num}</div>
                  <div style={{ fontSize: '7px', color: textMuted, letterSpacing: '2px', marginTop: '3px', fontWeight: 700, fontFamily: 'sans-serif' }}>{lbl}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side */}
        <div className="top-bar-right">
          {isMobile && (
            <button onClick={() => setMobileNavOpen(o => !o)} style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, padding: '8px 10px', cursor: 'pointer', color: textSecondary, fontSize: '14px', lineHeight: 1 }}>
              {mobileNavOpen ? '✕' : '☰'}
            </button>
          )}
          {!isMobile && (
            <>
              {onOpenMap && (
                <button onClick={onOpenMap} title="Open the urban-intelligence Map view"
                  style={{ backgroundColor: 'transparent', border: `1px solid ${gold}`, color: gold, padding: '6px 14px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all 0.19s' }}>
                  Map
                </button>
              )}
              {onOpenBoard && (
                <button onClick={onOpenBoard} title="Open the freeform Board — your infinite canvas workspace"
                  style={{ backgroundColor: gold, border: `1px solid ${gold}`, color: '#fff', padding: '6px 14px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all 0.19s' }}>
                  Board
                </button>
              )}
              <button onClick={openLibrary} title="Library — Read Order, Markets, Properties, Journal, Glossary, Sources"
                style={{ backgroundColor: 'transparent', border: `1px solid ${gold}`, color: gold, padding: '6px 14px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all 0.19s' }}>
                Library
              </button>
              <div className="top-bar-avatar">{(userName || username || 'U')[0].toUpperCase()}</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: textPrimary, fontFamily: "'EB Garamond', Georgia, serif" }}>{userName || username}</div>
                <div style={{ fontSize: '8px', color: roleColor, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, fontFamily: 'sans-serif' }}>{userRole}</div>
              </div>
              <button onClick={onLogout} style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: textSecondary, padding: '6px 14px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1px', textTransform: 'uppercase', transition: 'all 0.19s' }}>
                Sign Out
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {isMobile && mobileNavOpen && (
        <div style={{ backgroundColor: '#fafaf8', borderBottom: `1px solid ${border}`, position: 'sticky', top: '52px', zIndex: 99 }}>
          {filteredNav.map(n => (
            <button key={n.id} onClick={() => navTab(n.id)}
              style={{ width: '100%', textAlign: 'left', padding: '13px 20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: activeTab === n.id ? 700 : 500, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: 'sans-serif', backgroundColor: activeTab === n.id ? goldDim : 'transparent', color: activeTab === n.id ? gold : textSecondary, borderLeft: `2px solid ${activeTab === n.id ? gold : 'transparent'}`, transition: 'all 0.15s' }}>
              {n.label}
            </button>
          ))}
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10 }}>
            <button onClick={() => { setMobileNavOpen(false); openLibrary(); }} style={{ backgroundColor: 'transparent', border: `1px solid ${gold}`, color: gold, padding: '8px 16px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1px', textTransform: 'uppercase' }}>Library</button>
            <button onClick={onLogout} style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: textSecondary, padding: '8px 16px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', letterSpacing: '1px', textTransform: 'uppercase' }}>Sign Out</button>
          </div>
        </div>
      )}

      {/* ── MAIN AREA = left sidebar + content ── */}
      <div className="main-area" style={{ height: 'calc(100vh - 56px)' }}>

        {/* ── LEFT SIDEBAR (desktop only) ── */}
        {!isMobile && (
          <div className="left-sidebar">
            {filteredNav.map((n, i) => {
              const shape = NAV_SHAPES[n.id] || 'circle';
              const active = activeTab === n.id;
              return (
                <React.Fragment key={n.id}>
                  {/* Divider before admin */}
                  {n.id === 'admin' && <div className="nav-divider" />}
                  <button
                    className={`nav-item ${active ? 'active' : ''}`}
                    onClick={() => navTab(n.id)}
                    title={n.label}
                  >
                    <NavShape shape={shape} active={active} />
                    <div className="nav-tooltip">{n.label}</div>
                    {/* Unreviewed badge on alerts */}
                    {n.id === 'market' && (
                      <div style={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', backgroundColor: green, animation: 'pulse 2.5s ease-in-out infinite' }} />
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── CONTENT ── */}
        <div className="content-area">
          <div style={{
            maxWidth: activeTab === 'market' ? '100%' : '1200px',
            margin: '0 auto',
            padding: activeTab === 'market' ? '0' : (isMobile ? '24px 20px' : '44px 52px'),
            height: activeTab === 'market' ? '100%' : 'auto',
          }}>




        {/* MARKETS
            CHANGE 7: Two-column layout with SavedAnalysesPanel on the right.
            On desktop the panel is a fixed 280px sidebar; on mobile it stacks
            below the analysis content. The panel is always visible — users can
            load saved analyses immediately on arrival without running a new one.
            onLoadAnalysis restores both marketResult (the body) and msa (the
            selected market button) so the UI looks exactly as it did when the
            analysis was originally generated. */}
        {/* TERMINAL (replaces Markets) */}
        {activeTab === 'market' && (
          <div style={{ margin: '24px 0px 0px 20px', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Terminal token={token} />
          </div>
        )}

        {/* MARKET SEARCHER */}
        {activeTab === 'searcher' && (
          <div style={{ margin: '0', height: 'calc(100vh - 56px)', overflowY: 'auto' }}>
            <div style={{ padding: '16px 32px 0' }}>
              <DailyBrief token={token} onOpenLibrary={openLibrary} />
            </div>
            <Searcher token={token} />
          </div>
        )}

        {/* EMERGING MARKETS — Session 3 thesis-scorer tier view */}
        {activeTab === 'emerging' && (
          <div style={{ margin: '0', height: 'calc(100vh - 56px)', overflowY: 'auto' }}>
            <div style={{ padding: '16px 32px 0' }}>
              <DailyBrief token={token} onOpenLibrary={openLibrary} />
            </div>
            <EmergingMarkets token={token} />
          </div>
        )}

        {/* PEERS — Action Tracker */}
        {activeTab === 'peers' && (
          <div style={{ margin: '0', height: 'calc(100vh - 56px)', overflowY: 'auto' }}>
            <div style={{ padding: '16px 32px 0' }}>
              <DailyBrief token={token} onOpenLibrary={openLibrary} />
            </div>
            <Peers token={token} />
          </div>
        )}

        {/* FIRMS — IAPD-registered RE adviser directory */}
        {activeTab === 'firms' && (
          <div style={{ margin: '0', height: 'calc(100vh - 56px)', overflowY: 'auto' }}>
            <div style={{ padding: '16px 32px 0' }}>
              <DailyBrief token={token} onOpenLibrary={openLibrary} />
            </div>
            <Firms token={token} />
          </div>
        )}


        {/* DEAL PIPELINE KANBAN
            CHANGE 8a: New render block. KanbanBoard is fully self-contained —
            it fetches its own data and manages its own drag state. We only
            pass token and the cross-tab navigation callback. */}
        {activeTab === 'kanban' && (
          <KanbanBoard token={token} onDealClick={handleKanbanDealClick} />
        )}




        {/* ADMIN */}
        {activeTab === 'admin' && isAdmin && (
          <div>
            <div style={sl}>System</div>
            <div style={pt}>Admin Panel</div>
            <div style={ps}>Manage users, monitor deals, configure scoring weights, and view platform stats.</div>
            <div style={{ display: 'flex', borderBottom: `1px solid ${border}`, marginBottom: '24px', overflowX: 'auto' }}>
              {(['stats', 'users', 'deals', 'scoring', 'invites', 'mfa', 'branding'] as const).map(t => (
                <button key={t} onClick={() => setAdminTab(t)} style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 700, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '1.5px', backgroundColor: 'transparent', color: adminTab === t ? textPrimary : textMuted, borderBottom: `2px solid ${adminTab === t ? gold : 'transparent'}`, whiteSpace: 'nowrap' }}>{t}</button>
              ))}
              {adminTab !== 'scoring' && adminTab !== 'invites' && adminTab !== 'mfa' && adminTab !== 'branding' && (
                <button onClick={loadAdminData} style={{ marginLeft: 'auto', ...btn(), padding: '8px 16px', fontSize: '10px' }}>Refresh</button>
              )}
            </div>
            {adminLoading && adminTab !== 'scoring' && <div style={{ color: textMuted, fontSize: '11px' }}>Loading...</div>}
            {adminTab === 'stats' && adminStats && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1px', marginBottom: '16px', backgroundColor: border }}>
                  {[['TOTAL USERS', adminStats.total_users], ['ACTIVE USERS', adminStats.active_users], ['TOTAL DEALS', adminStats.total_deals], ['UNITS', adminStats.total_units?.toLocaleString() || 0]].map(([label, value]) => (
                    <div key={label as string} style={{ backgroundColor: '#fafaf8', padding: isMobile ? '16px' : '24px 28px' }}>
                      <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>{label}</div>
                      <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: 900, color: gold }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '18px 20px', backgroundColor: '#ffffff', border: `1px solid ${border}`, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: textPrimary, marginBottom: '3px' }}>Weekly Portfolio Digest</div>
                    <div style={{ fontSize: '11px', color: textMuted }}>Auto-runs every Monday at 8am.</div>
                  </div>
                  <button onClick={async () => { try { const res = await axios.post(`${API}/admin/send-digest`, {}, { headers }); alert(res.data.message || res.data.error || 'Sent.'); } catch { alert('Failed.'); } }} style={goldBtn}>Send Digest Now</button>
                </div>
              </div>
            )}
            {adminTab === 'users' && (
              <div style={{ overflowX: 'auto', backgroundColor: surface, border: `1px solid ${border}` }}>
                {adminUsers.length === 0 ? <div style={{ color: textMuted, fontSize: '12px', textAlign: 'center', padding: '40px' }}>No users. Click Refresh.</div> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '700px' }}>
                    <thead><tr style={{ backgroundColor: '#fafaf8' }}>{['Name', 'Username', 'Email', 'Joined', 'Status', 'Role', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {adminUsers.map(u => (
                        <tr key={u.id}>
                          <td style={{ ...td, color: textPrimary, fontWeight: 700 }}>{u.name}</td>
                          <td style={td}>{u.username}</td>
                          <td style={td}>{u.email}</td>
                          <td style={{ ...td, color: textMuted, fontSize: '11px' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td style={td}><span style={{ fontSize: '9px', fontWeight: 800, color: u.is_active ? green : red, letterSpacing: '1px', textTransform: 'uppercase' }}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                          <td style={td}>
                            <select value={u.role || (u.is_admin ? 'admin' : 'analyst')} onChange={e => setUserRoleAdmin(u.id, e.target.value)}
                              style={{ backgroundColor: surfaceHigh, border: `1px solid ${border}`, color: roleColors[u.role as UserRole] || textSecondary, padding: '4px 8px', fontSize: '10px', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', textTransform: 'uppercase' }}>
                              <option value="analyst">Analyst</option>
                              <option value="reviewer">Reviewer</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td style={td}>
                            <button onClick={() => toggleUser(u.id)} style={{ ...outBtn, padding: '4px 10px', fontSize: '9px', color: u.is_active ? red : green, borderColor: u.is_active ? red : green }}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {adminTab === 'deals' && (
              <div style={{ overflowX: 'auto', backgroundColor: surface, border: `1px solid ${border}` }}>
                {adminDeals.length === 0 ? <div style={{ color: textMuted, fontSize: '12px', textAlign: 'center', padding: '40px' }}>No deals. Click Refresh.</div> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
                    <thead><tr style={{ backgroundColor: '#fafaf8' }}>{['Property', 'Location', 'Units', 'Price', 'Cap Rate', 'Status', 'Date'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {adminDeals.map(d => {
                        const cap = d.noi && d.purchase_price ? ((d.noi / d.purchase_price) * 100).toFixed(2) + '%' : '—';
                        return (
                          <tr key={d.id}>
                            <td style={{ ...td, color: textPrimary, fontWeight: 700 }}>{d.name}</td>
                            <td style={td}>{d.location}</td>
                            <td style={td}>{d.units?.toLocaleString()}</td>
                            <td style={td}>${d.purchase_price?.toLocaleString()}</td>
                            <td style={{ ...td, color: gold, fontWeight: 800 }}>{cap}</td>
                            <td style={td}><span style={{ fontSize: '9px', fontWeight: 800, color: STATUS_COLORS[d.status] || textSecondary, letterSpacing: '1px', textTransform: 'uppercase' }}>{d.status}</span></td>
                            <td style={{ ...td, color: textMuted, fontSize: '11px' }}>{new Date(d.created_at).toLocaleDateString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {adminTab === 'scoring' && <ScoringWeights token={token} />}
            {adminTab === 'invites' && <InvitePanel token={token} />}
            {adminTab === 'mfa' && <MFASetup token={token} />}
            {adminTab === 'branding' && (
              <FirmBranding
                token={token}
                onBrandingUpdate={(primary, _accent, name) => {
                  setFirmPrimaryColor(primary);
                  setFirmName(name);
                }}
              />
            )}
          </div>
        )}

        </div>{/* end content-area inner */}
        </div>{/* end content-area */}
      </div>{/* end main-area */}

      {/* Personal Library — Journal · Glossary · Required Reading */}
      <Library
        token={token}
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
      />
    </div>
  );
}